/**
 * Location API Service
 * Service layer for location data (countries, states, cities) API calls
 *
 * All location endpoints support the `lang` query parameter per API spec.
 * If not provided, defaults to current UI language.
 */

import apiClient from './axios.config';
import { LOCATION_ROUTES } from '@/constants/api.constants';
import i18n from '@/i18n/config';
import type {
  CountryListResponse,
  StateListResponse,
  CityListResponse,
  PostcodeLocationResponse,
  PostcodeLookupParams,
  AllLocationsResponse,
  AllLocationsParams,
} from '@/types/api/location';

/**
 * Get API language code from current UI language
 * API only accepts 'en' or 'zh', not regional variants like 'zh-TW'
 */
const getApiLanguage = (): 'en' | 'zh' => {
  return i18n.language.startsWith('zh') ? 'zh' : 'en';
};

/**
 * Location API service object
 */
export const locationApi = {
  /**
   * GET /api/locations/all
   * Fetch complete hierarchical location data (all countries, states, cities)
   * @param params - Query parameters: lang and typeId
   */
  getAllLocations: async (params?: AllLocationsParams): Promise<AllLocationsResponse> => {
    const response = await apiClient.get<AllLocationsResponse>(
      LOCATION_ROUTES.ALL,
      { params: { ...params, lang: params?.lang ?? getApiLanguage() } }
    );
    return response.data;
  },

  /**
   * GET /api/locations/countries
   * Fetch all available countries
   * @param lang - Optional language code (e.g., "en", "zh"). Defaults to UI language.
   */
  getCountries: async (lang?: string): Promise<CountryListResponse> => {
    const response = await apiClient.get<CountryListResponse>(
      LOCATION_ROUTES.COUNTRIES,
      { params: { lang: lang ?? getApiLanguage() } }
    );
    return response.data;
  },

  /**
   * GET /api/locations/countries/:countryCode/states
   * Fetch states/provinces by country code
   * @param countryCode - ISO 3166-1 alpha-2 code (e.g., "TW", "US", "AU")
   * @param lang - Optional language code (e.g., "en", "zh"). Defaults to UI language.
   */
  getStatesByCountry: async (countryCode: string, lang?: string): Promise<StateListResponse> => {
    const response = await apiClient.get<StateListResponse>(
      LOCATION_ROUTES.STATES_BY_COUNTRY(countryCode),
      { params: { lang: lang ?? getApiLanguage() } }
    );
    return response.data;
  },

  /**
   * GET /api/locations/states/:stateId/cities
   * Fetch cities by state ID
   * @param stateId - State ID
   * @param lang - Optional language code (e.g., "en", "zh"). Defaults to UI language.
   */
  getCitiesByState: async (stateId: number, lang?: string): Promise<CityListResponse> => {
    const response = await apiClient.get<CityListResponse>(
      LOCATION_ROUTES.CITIES_BY_STATE(stateId),
      { params: { lang: lang ?? getApiLanguage() } }
    );
    return response.data;
  },

  /**
   * GET /api/locations/postcode/:countryCode/:postcode
   * Lookup location information by postcode
   * @param params - Country code, postcode, and optional language
   */
  lookupByPostcode: async (
    params: PostcodeLookupParams
  ): Promise<PostcodeLocationResponse> => {
    const { countryCode, postcode, lang } = params;
    const response = await apiClient.get<PostcodeLocationResponse>(
      LOCATION_ROUTES.LOOKUP_BY_POSTCODE(countryCode, postcode),
      { params: { lang: lang ?? getApiLanguage() } }
    );
    return response.data;
  },
};

export default locationApi;
